import { Component, OnInit, OnDestroy, ComponentRef } from '@angular/core';
import { Router } from '@angular/router';
// MSAL_TOGGLE_START
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
// MSAL_TOGGLE_END
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { Variables, VariableType } from '~/core/classes/user/variables';

import { GraphService } from '~/core/services/graph.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { AppConfigService } from '~/core/services/appconfig.service';

import { AppSettings } from '~/core/models/appsettings';

export interface Modal {
    type?: ComponentRef<any>;
    inputs?: any;
    outputs?: any
}
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})

export class AppComponent implements OnInit, OnDestroy {
    private appSettings: AppSettings = new AppSettings();
    private readonly _destroying$ = new Subject<void>();

    public error: string = "";
    public title: string = "";
    public expand: boolean = false;

    public showNav: boolean = true;
    public showFooter: boolean = true;

    public layoutCssClass: string = "";

    public isLoggedIn: boolean = false;
    public isLoading: boolean = true;

    // Alert
    public showAlert: boolean = false;
    public alertTitle: string = "Alert";
    public alertMessage: string = "";
    public alertContinueText: string = "OK";
    public alertCancelText: string = "";

    // Modal
    public modal: Modal | null = null;

    constructor(
        // MSAL_TOGGLE_START
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        // MSAL_TOGGLE_END
        private graphService: GraphService,
        private router: Router,
        private appSettingsService: AppSettingsService,
        private appConfigService: AppConfigService
    ) {
        window.onbeforeunload = null;
        sessionStorage.removeItem("__allowNavigate__");

        setTimeout(() => {
            if (this.isLoggedIn) {
                this.layoutCssClass = Variables.get("Nav.LayoutCssClass", VariableType.Local) ?? "";
                this.setLayoutClass(false);
            }
        }, 1500);
    }

    //*************************************************************************
    //  Component Life-Cycle Methods
    //*************************************************************************
    public async ngOnInit() {
        await this.appSettingsService.getSettings(true).then(async (appSettings: AppSettings) => {
            if (appSettings) {
                this.appSettings = appSettings;
                this.title = this.appSettings.title;
                this.showNav = this.appSettings.showNav;
                this.showFooter = this.appSettings.showFooter;

                // MSAL_TOGGLE_START
                this.getMSALAccounts();
                // MSAL_TOGGLE_END

            } else {
                this.error = "Unable to load application settings.";
                this.isLoading = false;
            }
        });
    }

    public ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    //*************************************************************************
    //  Public Methods
    //*************************************************************************
    public setLayoutClass(change: boolean = true) {
        if (change) {
            if (this.layoutCssClass !== "") {
                this.layoutCssClass = "";
            } else {
                this.layoutCssClass = "expand";
            }
        }

        if (this.layoutCssClass === "") {
            this.expand = false;
        } else {
            setTimeout(() => {
                this.expand = true;
            }, 100);
        }

        Variables.set("Nav.LayoutCssClass", this.layoutCssClass, VariableType.Local);
    }

    public async reloadPage(page = "") {
        let currentUrl = page === "" ? this.router.url : page;

        this.router.navigate([currentUrl]);
    }

    public navigateTo(url: string) {
        this.router.navigateByUrl(url);
    }

    public navigateToRunFromBreadcrumb(): void {
        const currentUrl = this.getCurrentUrlPath();
        const hierarchyRunChildRouteMatch = currentUrl.match(
            /^\/refinery\/([^\/]+)\/unit\/([^\/]+)\/run\/([^\/]+)\/(?:guide|citations\/[^\/]+(?:\/guide)?)$/
        );

        if (hierarchyRunChildRouteMatch?.[1] && hierarchyRunChildRouteMatch?.[2] && hierarchyRunChildRouteMatch?.[3]) {
            this.router.navigate([
                '/refinery',
                decodeURIComponent(hierarchyRunChildRouteMatch[1]),
                'unit',
                decodeURIComponent(hierarchyRunChildRouteMatch[2]),
                'run',
                decodeURIComponent(hierarchyRunChildRouteMatch[3])
            ]);
            return;
        }

        const runChildRouteMatch = currentUrl.match(/^\/run\/([^\/]+)\/(?:guide|citations\/[^\/]+(?:\/guide)?)$/);

        if (runChildRouteMatch?.[1]) {
            this.router.navigate(['/run', decodeURIComponent(runChildRouteMatch[1])]);
        }
    }

    public navigateToCitationsFromBreadcrumb(): void {
        const currentUrl = this.getCurrentUrlPath();
        const hierarchyCitationsRouteMatch = currentUrl.match(
            /^\/refinery\/([^\/]+)\/unit\/([^\/]+)\/run\/([^\/]+)\/citations\/([^\/]+)\/guide$/
        );

        if (
            hierarchyCitationsRouteMatch?.[1]
            && hierarchyCitationsRouteMatch?.[2]
            && hierarchyCitationsRouteMatch?.[3]
            && hierarchyCitationsRouteMatch?.[4]
        ) {
            this.router.navigate([
                '/refinery',
                decodeURIComponent(hierarchyCitationsRouteMatch[1]),
                'unit',
                decodeURIComponent(hierarchyCitationsRouteMatch[2]),
                'run',
                decodeURIComponent(hierarchyCitationsRouteMatch[3]),
                'citations',
                decodeURIComponent(hierarchyCitationsRouteMatch[4])
            ]);
            return;
        }

        const citationsRouteMatch = currentUrl.match(/^\/run\/([^\/]+)\/citations\/([^\/]+)\/guide$/);

        if (citationsRouteMatch?.[1] && citationsRouteMatch?.[2]) {
            this.router.navigate([
                '/run',
                decodeURIComponent(citationsRouteMatch[1]),
                'citations',
                decodeURIComponent(citationsRouteMatch[2])
            ]);
        }
    }

    public get breadcrumbRunLabel(): string {
        const currentUrl = this.getCurrentUrlPath();
        const hierarchyRunMatch = currentUrl.match(
            /^\/refinery\/[^\/]+\/unit\/[^\/]+\/run\/([^\/]+)(?:\/(?:guide|citations\/[^\/]+(?:\/guide)?))?$/
        );

        if (hierarchyRunMatch?.[1]) {
            return `Run ${decodeURIComponent(hierarchyRunMatch[1])}`;
        }

        const runMatch = currentUrl.match(/^\/run\/([^\/]+)(?:\/(?:guide|citations\/[^\/]+(?:\/guide)?))?$/);

        if (!runMatch?.[1]) {
            return '';
        }

        return `Run ${decodeURIComponent(runMatch[1])}`;
    }

    public get isRunGuideRoute(): boolean {
        const currentUrl = this.getCurrentUrlPath();
        return /^\/run\/[^\/]+\/guide$/.test(currentUrl)
            || /^\/refinery\/[^\/]+\/unit\/[^\/]+\/run\/[^\/]+\/guide$/.test(currentUrl);
    }

    public get isRunCitationsRoute(): boolean {
        const currentUrl = this.getCurrentUrlPath();
        return /^\/run\/[^\/]+\/citations\/[^\/]+$/.test(currentUrl)
            || /^\/refinery\/[^\/]+\/unit\/[^\/]+\/run\/[^\/]+\/citations\/[^\/]+$/.test(currentUrl);
    }

    public get isRunCitationsGuideRoute(): boolean {
        const currentUrl = this.getCurrentUrlPath();
        return /^\/run\/[^\/]+\/citations\/[^\/]+\/guide$/.test(currentUrl)
            || /^\/refinery\/[^\/]+\/unit\/[^\/]+\/run\/[^\/]+\/citations\/[^\/]+\/guide$/.test(currentUrl);
    }

    public get isHierarchyRefineryRoute(): boolean {
        return /^\/refinery\/[^\/]+$/.test(this.getCurrentUrlPath());
    }

    public get isHierarchyUnitRoute(): boolean {
        return /^\/refinery\/[^\/]+\/unit\/[^\/]+$/.test(this.getCurrentUrlPath());
    }

    public get isHierarchyRunRoute(): boolean {
        return /^\/refinery\/[^\/]+\/unit\/[^\/]+\/run\/[^\/]+$/.test(this.getCurrentUrlPath());
    }

    public get breadcrumbRefineryLabel(): string {
        const match = this.getCurrentUrlPath().match(/^\/refinery\/([^\/]+)/);
        return match?.[1] ? decodeURIComponent(match[1]).toUpperCase() : '';
    }

    public get breadcrumbUnitLabel(): string {
        const match = this.getCurrentUrlPath().match(/^\/refinery\/[^\/]+\/unit\/([^\/]+)/);
        return match?.[1] ? decodeURIComponent(match[1]).toUpperCase() : '';
    }

    public get breadcrumbHierarchyRunLabel(): string {
        const match = this.getCurrentUrlPath().match(/^\/refinery\/[^\/]+\/unit\/[^\/]+\/run\/([^\/]+)/);
        return match?.[1] ? `Run ${decodeURIComponent(match[1])}` : '';
    }

    public navigateToRefineryFromBreadcrumb(): void {
        const refineryLabel = this.breadcrumbRefineryLabel;

        if (refineryLabel) {
            this.router.navigate(['/refinery', refineryLabel.toLowerCase()]);
        }
    }

    public navigateToUnitFromBreadcrumb(): void {
        const refineryLabel = this.breadcrumbRefineryLabel;
        const unitLabel = this.breadcrumbUnitLabel;

        if (refineryLabel && unitLabel) {
            this.router.navigate(['/refinery', refineryLabel.toLowerCase(), 'unit', unitLabel.toLowerCase()]);
        }
    }

    public get currentBreadcrumbLabel(): string {
        const currentUrl = this.getCurrentUrlPath();

        if (this.isRunCitationsGuideRoute) {
            return 'Guide';
        }

        if (this.isRunGuideRoute) {
            return 'Guide';
        }

        if (this.isRunCitationsRoute) {
            return 'Citations';
        }

        const runRouteMatch = currentUrl.match(/^\/run\/([^\/]+)$/);
        if (runRouteMatch?.[1]) {
            return this.breadcrumbRunLabel;
        }

        if (currentUrl === '/guide') {
            return 'Guide';
        }

        return '';
    }

    public get isHomeRoute(): boolean {
        const currentUrl = this.getCurrentUrlPath();
        return currentUrl === '/';
    }

    public get breadcrumbCitationAssetLabel(): string {
        const currentUrl = this.getCurrentUrlPath();
        const citationsGuideMatch = currentUrl.match(/^\/run\/[^\/]+\/citations\/([^\/]+)\/guide$/);

        if (!citationsGuideMatch?.[1]) {
            return '';
        }

        return 'Citations';
    }

    //*************************************************************************
    //  Alert Methods
    //*************************************************************************
    public alertContinue() {
        this.showAlert = false;
    }

    public alertCancel() {
        this.showAlert = false;
    }

    private getCurrentUrlPath(): string {
        return this.router.url.split('?')[0].split('#')[0];
    }


    // MSAL_TOGGLE_START
    private getMSALAccounts() {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                let account: AccountInfo | null = this.authService.instance.getActiveAccount();
    
                if (account === null) {
                    account = this.getAccountInfo();
                }
    
                this.setAccountInfo(account);
            });
    }
    // MSAL_TOGGLE_END

    // MSAL_TOGGLE_START
    private getAccountInfo() {
        let accounts = this.authService.instance.getAllAccounts();
        let account: AccountInfo | null = null;
    
        if (accounts.length > 0) {
            this.authService.instance.setActiveAccount(accounts[0]);
            account = accounts[0];
        }
    
        return account;
    }
    // MSAL_TOGGLE_END

    // MSAL_TOGGLE_START
    private async setAccountInfo(account: AccountInfo | null) {
        let allowAccess: boolean = true; // Allow access by default
        let roles: any = [];
        
        // Get role from appConfig (can be group ID or group name)
        const roleConfig = this.appConfigService.getConfig('role');

        if (roleConfig) {
            // Check if roleConfig is a GUID (group ID) or a name
            const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(roleConfig);
            
            if (isGuid) {
                // Use group ID directly
                const isInGroup = await this.graphService.checkUserInGroup(roleConfig);

                if (isInGroup) {
                    roles.push(roleConfig);
                    console.log('User has access - Role ID:', roleConfig);
                } else {
                    allowAccess = false;
                    console.log('User not in group ID:', roleConfig, '- access denied');
                }
            } else {
                // Search by group name
                const groupAccess = await this.graphService.getUserGroups(roleConfig);

                if (groupAccess?.value.length > 0) {
                    // User is in a group that matches the name
                    const groupId = groupAccess.value[0].id;
                    roles.push(groupId);
                    console.log('User has access - Role:', roleConfig, '| Group ID:', groupId);
                } else {
                    allowAccess = false;
                    console.log('User not in group:', roleConfig, '- access denied');
                }
            }
        }

        if (account) {
            await this.setUserInfo(account, roles);
            // Clear old settings and save fresh data
            this.appSettings.user.roles = roles;
            await this.appSettingsService.saveSettings(this.appSettings);
        }

        if (allowAccess) {
            this.layoutCssClass = "";
            this.isLoggedIn = true;
        } else {
            // Clear user data when access is denied and redirect to access-denied page
            this.appSettings.user = null as any;
            await this.appSettingsService.saveSettings(this.appSettings);
            this.layoutCssClass = "noaccess";
            this.isLoggedIn = false;
            await this.router.navigate(['/access']);
        }

        this.isLoading = false;
    }
    // MSAL_TOGGLE_END

    // MSAL_TOGGLE_START
    private async setUserInfo(account: any, roles: any) {
        account.photo = "";
    
        if (account.name) {
            const name: string[] = account.name.split(", ");
    
            if (name && name.length > 1) {
                const firstName: string = name[1].split(" (")[0].split(" [")[0];
                const lastName: string = name[0];
    
                account.firstName = firstName;
                account.lastName = lastName;
                account.name = `${firstName} ${lastName}`;
                account.initials = firstName.substring(0, 1).toUpperCase() + lastName.substring(0, 1).toUpperCase();
            }
        }
    
        account.roles = roles;
    
        this.appSettings.user = account;
    }
    // MSAL_TOGGLE_END
}
 